import { proxy } from 'valtio';

import type { InsertSelectionMentionV3Fn } from '../chat-hooks/useInsertSelectionMentionV3';
import { ACTIVE_NEW_CHAT_DRAFT_KEY, readChatDraft } from '../ChatPanel/chat-draft-storage';
import type { DocumentMentionElement } from '../ChatPanel/ChatEditor/ChatEditorMention/utils';
import type { PinnedSkill } from '../ChatPanel/ChatEditor/ChatEditorSkill/utils';
import type { AIChatItem } from '../ChatPanelMarkdown/helpers/aiChatItem';

/**
 * We use Valtio for state management because it is easy to deeply update state
 * without having to use { ...stuff, [...moreStuff, newValue ] } in many places.
 * It's like `immer` but for state objects.
 */

/**
 * https://github.com/pmndrs/valtio/issues/327#issuecomment-1035937848
 *
 * Type snapshot same as proxy object to avoid type errors due to readonly
 *
 * NOTE: We are using Valtio only here but this should be moved to a global
 * location if we start using it in more places.
 */
declare module 'valtio' {
  function useSnapshot<T extends object>(p: T): T;
}

/**
 * Document context for text selection in the editor.
 * Contains all info needed to create a document mention with selection metadata.
 */
export interface SelectedDocument {
  id: string;
  name: string;
  selectedText: string;
  wordCount: number;
}

export type ChatThreadState = {
  autoScrollAIChatItem: AIChatItem | null;
  /**
   * Increments whenever the set of full-document chips in the chat input
   * changes (chips added, or removed via the chip's 'x' / backspace). The
   * repository "Add to Chat" / "Remove from Chat" toggle lives in a separate
   * component tree and reads the chip set synchronously at render time, so it
   * subscribes to this counter to re-render and recompute its label when chips
   * change directly inside the input.
   */
  documentChipVersion: number;
  /**
   * Increments every time the chat input opens one of its popovers
   * (currently the history popover). Components that
   * float above the input — e.g. `ThreadDeliverablesBar` — watch this
   * counter to collapse themselves when an input popover takes focus.
   */
  inputPopoverOpenCount: number;
  newChatDraftId: string;
  /**
   * Document context for the current text selection.
   * Contains document info, selected text, and word count.
   * Null when no text is selected.
   */
  selectedDocument: SelectedDocument | null;
  /**
   * We add `undefined` here because we knowingly will access keys that don't
   * exist and `undefined` is a valud return value that basically indicates the
   * `default` value for visibility depending on the suggestion type. For
   * example, accepted and rejected suggestions are hidden by default while
   * suggestions not in the document are visible by default.
   *
   * NOTE: We don't use a boolean to avoid the potential issue of `undefined`
   * being a falsy value when we intend it to mean `default` for the visibility.
   */
  suggestionVisibilityByGroupId: Record<string, 'visible' | 'hidden' | undefined>;
  /**
   * Loading-pulse copy scoped to the assistant message it belongs to. Without
   * the messageId scope, a "Building plan..." event for turn B could render
   * the pulse under turn A's bubble during back-to-back sends, and trivial
   * turns would show a stale pulse from a sibling thread that's still in
   * "Working on request...".
   */
  thinkingMessage: { messageId: string; text: string } | null;
};

/**
 * State for the chat panel.
 */
export const $chatThreadState = proxy<ChatThreadState>({
  autoScrollAIChatItem: null,
  documentChipVersion: 0,
  inputPopoverOpenCount: 0,
  newChatDraftId: readChatDraft(ACTIVE_NEW_CHAT_DRAFT_KEY),
  selectedDocument: null,
  suggestionVisibilityByGroupId: {},
  thinkingMessage: null,
});

/**
 * This is specfically to alllow the refine suggestions to be sent to the chat
 * thread.
 *
 * We don't include it in the Valtio state since this should never be used to
 * initiate a re-render of any component. It's just easier to store these
 * callbacks in their own object.
 */
export type ChatCallbacks = {
  /**
   * Returns the document IDs of full-document chips in the active chat input,
   * excluding selection / selection-v3 mentions. Use this to toggle document
   * chips so a selection mention isn't mistaken for a present chip.
   */
  getDocumentChipIds: (() => string[]) | null;
  /** Returns true if the mentions were inserted, false if the editor wasn't ready. */
  insertDocumentMentions: ((mentions: DocumentMentionElement[]) => boolean) | null;
  insertSelectionMentionV3: InsertSelectionMentionV3Fn | null;
  insertSkill: ((skill: PinnedSkill) => boolean) | null;
  /** Holds mentions queued before the ChatPanel has mounted and registered insertDocumentMentions. */
  pendingDocumentMentions: DocumentMentionElement[] | null;
  /**
   * A draft queued before the ChatPanel registered setPromptDraft, or while
   * its pane was hidden: the editor's playbook drawer hands the chat a prompt
   * before the chat pane is the one showing.
   */
  pendingPromptDraft: string | null;
  pendingSkill: PinnedSkill | null;
  removeDocumentMentions: ((ids: string[]) => void) | null;
  sendUserMessage: ((text: string) => Promise<boolean>) | null;
  /**
   * Replaces whatever is in the active chat input with this markdown, mention
   * links included, and leaves the caret at the end so the user only has to
   * press send. Returns false when the input's editor is not ready.
   */
  setPromptDraft: ((markdown: string) => boolean) | null;
};

export const $chatCallbacks: ChatCallbacks = {
  getDocumentChipIds: null,
  insertDocumentMentions: null,
  insertSelectionMentionV3: null,
  insertSkill: null,
  pendingDocumentMentions: null,
  pendingPromptDraft: null,
  pendingSkill: null,
  removeDocumentMentions: null,
  sendUserMessage: null,
  setPromptDraft: null,
};
